import React, { forwardRef, useImperativeHandle, useMemo, useRef, useCallback } from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { isEqual } from 'lodash';

const { createFormField } = Form;

/**
 * @description 以参数component创建一个带有value, onChange和ref的数组组件, 用于简化antd数组类型表单的编写, 和将表单业务逻辑与实现逻辑(指实现动态数组域)分离
 *              实现思路主要是利用闭包访问和更新保存在此组件中的表单域的状态
 * @param component 一个实现了React.forwardRef接口和antd Form接口的组件
 * @param componentProps 一个包含创建component组件的参数对象, 此参数尽量需要memo化, 因为这个参数的变动会使被创建的component组件重新渲染, 导致失去焦点等问题
 * @param name 用于调用antd Form.create()方法的name属性, 理论上不是必须, 但可以在debug时方便打印具体代理渲染的表单域
 * @param ref antd.Form[], 用于收集数组中每个子表单的实例, 可以自己获取来单独操作子表单
 * @param onChange 不需要传, 设计是留给上层form.getFieldDecorator传入 [由上层组件自动传入]
 * @param value 不需要传, 设计是留给上层form.getFieldDecorator传入 [由上层组件自动传入]
 * @returns 组件实例
 */
const ArrayField = forwardRef(({ component, componentProps, onChange, value = [], name }, ref) => {
  // 这个地方使用ref保存状态的原因:
  // - useEffect的运行时机不确定会导致渲染不符合预期, 加上父子组件状态同步容易导致循环渲染, 只想到这个办法
  // - 防止组件重新渲染
  const onChangeRef = useRef(onChange);
  // 整个数组的值, 当数组中某个值更新时, 通过onChange通知父组件时需要带上其他值
  const valueRef = useRef(value);
  // 数组中全部子表单的表单状态, 子表单可以通过mapPropsToFields直接渲染
  const fieldsRef = useRef([]);
  // 子表单实例数组
  const formRef = useRef([]);

  valueRef.current = value;

  // 以value更新fields
  fieldsRef.current = value.map((outerValue, i) => {
    const innerFields = fieldsRef.current[i];

    if (innerFields) {
      const newEntries = Object.entries(outerValue).map(([key, outerFieldValue]) => {
        const targetInnerField = innerFields[key];
        if (!targetInnerField) {
          return [key, createFormField({ value: outerFieldValue })];
        }
        if (!isEqual(targetInnerField.value, outerFieldValue)) {
          return [
            key,
            createFormField({
              ...targetInnerField,
              value: outerFieldValue,
              touched: true,
              dirty: true
            })
          ];
        }
        return [key, targetInnerField];
      });
      return Object.fromEntries(newEntries);
    }

    return Object.entries(outerValue).reduce((acc, [key, outerFieldValue]) => {
      acc[key] = createFormField({ value: outerFieldValue });
      return acc;
    }, {});
  });

  // 将收集到的子表单全部通过ref返回给装饰器, 这样父表单可以通过form.getFieldInstance拿到所有子表单
  useImperativeHandle(ref, () => formRef.current, [formRef.current.length]);

  // 用memo的原因是, 如果组件重新渲染会导致丢失焦点
  const components = useMemo(() => {
    const result = [];

    for (let i = 0; i < value.length; i++) {
      const key = `${name}[${i}]`;

      const cps = componentProps ?? {};
      // 删除 | 修改 功能会用到...所以直接加了
      cps.fieldIndex = i;

      const Component = Form.create({
        name: key,
        onValuesChange(props, changedValues, allValues) {
          const values = Array.from(valueRef.current);
          values.splice(i, 1, allValues);
          try {
            onChangeRef.current(values);
          } catch (err) {
            console.error(err);
          }
        },
        onFieldsChange(props, fields, allValues) {
          fieldsRef.current[i] = allValues;
        },
        mapPropsToFields() {
          return fieldsRef.current[i];
        }
      })(component);

      result.push(<Component {...cps} key={key} wrappedComponentRef={form => (formRef.current[i] = form)} />);
    }
    return result;
  }, [value.length, component, componentProps, name]);

  return components;
});

/**
 * @description 因为此种方案会有多个Form实例, 此hook可以将父表单与子表单连接起来, 实现当父表单校验时, 子表单也自动校验
 * @param {*} form ArrayField所在的Form实例
 * @param {*} fieldName 装饰ArrayField绑定的表单域名
 * @param {*} multi 当前组件是否是复合校验
 * @returns 用于父表单的getFieldDecorator配置参数的自定义校验的函数: rules[*].validator
 */
function useFieldValidator(form, fieldName, multi = false) {
  const formRef = useRef(form);
  formRef.current = form;

  const validator = useCallback((rule, value, callback) => {
    // 测试发现同步校验的时机是在父子表单同步数据的过程中, 导致校验结果不对
    setTimeout(() => {
      const subForms = formRef.current.getFieldInstance(fieldName);

      if (!subForms || subForms.length === 0) {
        return callback();
      }
      const errs = [];
      subForms?.forEach?.(sf => {
        sf?.validateFields?.(error => {
          if (error) {
            errs.push(error);
          }
        });
      });
      if (multi) {
        callback();
      }
      if (errs.length === 0) {
        // 只有当成功的时候才调用callback,
        // 失败的时候不需要是因为子表单是完整的表单, 有自己的错误提示, 父组件不应该也无法处理(会报错)这类错误
        callback();
      }
    }, 0);
  }, []);

  return validator;
}

ArrayField.useFieldValidator = useFieldValidator;

export { ArrayField, useFieldValidator };
